import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Settings, LogOut, Building2, Lock, User, Crown, Plus } from 'lucide-react';
import { toast } from 'sonner';
import { useLanguage } from '@/contexts/LanguageContext';
import LanguageSwitcher from '@/components/LanguageSwitcher';
import AdminSettingsDialog from '@/components/AdminSettingsDialog';
import { useTheme } from '@/hooks/useTheme';
import companyService from '@/services/companyService';
import authService from '@/services/authService';
import LoadingSpinner from '@/components/LoadingSpinner';

interface Company {
  id: number;
  name: string;
  logo_url?: string;
}

interface UserRole {
  role: string;
  scope: string;
}

const CompanySelection = () => {
  const [userEmail, setUserEmail] = useState('');
  const [userRole, setUserRole] = useState('');
  const [isAdmin, setIsAdmin] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const navigate = useNavigate();
  const { t } = useLanguage();
  const { isLoading: themeLoading, isInitialized: themeInitialized } = useTheme();

  const [companies, setCompanies] = useState<Company[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const email = localStorage.getItem('userEmail');
    const role = localStorage.getItem('userRole');
    const companyIdsStr = localStorage.getItem('companyIds');

    if (!email || !role) {
      navigate('/login');
      return;
    }

    const companyIds = companyIdsStr ? JSON.parse(companyIdsStr) : [];
    setUserEmail(email);
    setUserRole(role);
    
    const adminCheck = role === 'admin';
    setIsAdmin(adminCheck);
    
    fetchCompanies(companyIds, adminCheck);
  }, [navigate]);

  const fetchCompanies = async (companyIds: string[], isAdminUser: boolean) => {
    setLoading(true);
    try {
      // Wait for AuthService to be fully initialized before fetching companies
      let retries = 0;
      const maxRetries = 10;
      
      while (retries < maxRetries) {
        const currentUser = authService.getCurrentUser();
        const userProfile = authService.getUserProfile();
        
        if (currentUser && userProfile) {
          // AuthService is ready, safe to fetch companies
          break;
        }
        
        // Wait 100ms and retry
        await new Promise(resolve => setTimeout(resolve, 100));
        retries++;
      }
      
      if (retries >= maxRetries) {
        throw new Error('身份驗證服務未就緒，請重新登入');
      }
      
      const companiesData = await companyService.getUserCompanies();
      
      // Convert string IDs to numbers for compatibility
      const formattedCompanies = companiesData.map(company => ({
        id: parseInt(company.id, 10),
        name: company.name,
        logo_url: company.logo_url,
      }));
      
      setCompanies(formattedCompanies);

    } catch (err: any) {
      console.error('Error fetching companies:', err);
      toast.error(err.message || '載入公司失敗');
      
      // If authentication failed, redirect to login
      if (err.message?.includes('身份驗證') || err.message?.includes('用戶未登入')) {
        navigate('/login');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCompanySelect = (company: Company) => {
    localStorage.setItem('selectedCompanyId', String(company.id));
    localStorage.setItem('selectedCompanyName', company.name);
    localStorage.setItem('currentCompanyRole', userRole); // The role is global
    
    navigate(`/dashboard`);
  };

  const handleLogout = () => {
    localStorage.clear();
    toast.success(t('loggedOutSuccessfully'));
    navigate('/login');
  };
  
  const getCompanyIcon = (companyName: string) => {
    const icons = [Building2];
    return icons[0];
  };

  // Show loading spinner until theme is initialized to prevent flash
  if (!themeInitialized || themeLoading) {
    return <LoadingSpinner fullScreen text="Loading theme..." />;
  }

  return (
    <div className="min-h-screen relative overflow-hidden bg-background">
      {/* Animated Background */}
      <div className="absolute inset-0 bg-gradient-to-br from-background via-purple-900/5 to-background">
        <div 
          className="absolute inset-0 opacity-40"
          style={{
            backgroundImage: "url(\"data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%239C92AC' fill-opacity='0.1'%3E%3Ccircle cx='30' cy='30' r='2'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E\")"
          }}
        ></div>
        <div className="absolute top-0 left-0 w-full h-full">
          <div className="animate-pulse absolute top-20 left-20 w-72 h-72 bg-purple-500/10 rounded-full blur-3xl"></div>
          <div className="animate-pulse absolute bottom-20 right-20 w-96 h-96 bg-blue-500/10 rounded-full blur-3xl animation-delay-2000"></div>
        </div>
      </div>

      {/* Header */}
      <div className="relative z-10 flex justify-between items-center p-6">
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2 text-muted-foreground">
            {isAdmin ? <Crown className="w-5 h-5 text-yellow-400" /> : <User className="w-5 h-5" />}
            <span>{t('welcome')}, {userEmail}</span>
          </div>
        </div>
        
        <div className="flex items-center gap-3">
          <LanguageSwitcher />
          {isAdmin && (
            <>
              <Button
                onClick={() => navigate('/company-management')}
                variant="outline"
                size="sm"
                className="bg-card/50 border-border text-muted-foreground hover:bg-secondary/50 hover:text-foreground"
              >
                <Plus className="w-4 h-4 mr-2" />
                {t('companyManagement')}
              </Button>
              <Button
                onClick={() => setShowSettings(true)}
                variant="outline"
                size="sm"
                className="bg-card/50 border-border text-muted-foreground hover:bg-secondary/50 hover:text-foreground"
              >
                <Settings className="w-4 h-4 mr-2" />
                {t('settings')}
              </Button>
            </>
          )}
          <Button
            onClick={handleLogout}
            variant="outline"
            size="sm"
            className="bg-card/50 border-border text-muted-foreground hover:bg-secondary/50 hover:text-foreground"
          >
            <LogOut className="w-4 h-4 mr-2" />
            {t('logout')}
          </Button>
        </div>
      </div>

      {/* Main Content */}
      <div className="relative z-10 p-6">
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold bg-gradient-to-r from-purple-400 to-cyan-400 bg-clip-text text-transparent mb-2">
            {t('selectCompany')}
          </h1>
          <p className="text-muted-foreground text-lg">{t('chooseCompany')}</p>
        </div>

        {/* Company Grid */}
        <div className="max-w-6xl mx-auto">
          {loading ? (
            <div className="text-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-400 mx-auto mb-4"></div>
              <p className="text-muted-foreground">載入公司列表中...</p>
            </div>
          ) : companies.length === 0 ? (
            <div className="text-center py-12">
              <Lock className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-foreground mb-2">沒有可存取的公司</h3>
              <p className="text-muted-foreground">您目前沒有被授權存取任何公司。請聯繫管理員分配權限。</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {[...companies].sort((a, b) => String(a.id).localeCompare(String(b.id))).map((company) => {
                const IconComponent = getCompanyIcon(company.name);
                
                return (
                  <div
                    key={company.id}
                    className="bg-card rounded-2xl shadow-lg border border-border p-4 flex flex-col items-center transition-transform duration-200 hover:scale-105 hover:shadow-purple-400/30"
                  >
                    <div className="w-full flex justify-center items-start mb-3" style={{ minHeight: 80 }}>
                      {company.logo_url ? (
                        <img
                          src={company.logo_url}
                          alt={company.name}
                          className="w-[90%] max-h-24 object-contain rounded-xl"
                          style={{ marginTop: 0 }}
                        />
                      ) : (
                        <IconComponent className="w-10 h-10 text-muted-foreground" />
                      )}
                    </div>
                    <div className="text-lg font-bold text-foreground mb-2 text-center">{company.name}</div>
                    <button
                      className="mt-2 px-6 py-2 rounded-full bg-gradient-to-r from-purple-600 to-cyan-500 text-white font-semibold shadow hover:from-purple-700 hover:to-cyan-600 transition-all"
                      onClick={() => handleCompanySelect(company)}
                    >
                      {t('clickToAccess')}
                    </button>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Admin Settings Dialog */}
        <AdminSettingsDialog 
          open={showSettings} 
          onOpenChange={setShowSettings} 
        />
      </div>
    </div>
  );
};

export default CompanySelection;
